import { useEffect, useState, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { getPreferredMerchantAssociation, normalizeShopDomain } from '@/lib/merchantSelection';
import { clearMerchantSupportSession, getMerchantSupportSession } from '@/lib/merchantSupportSession';

export type MerchantRole = 'super_admin' | 'manager' | 'view_only' | null;

interface ShopifyMerchantState {
  role: MerchantRole;
  loading: boolean;
  merchantId: string | null;
  isSuperAdmin: boolean;
  isManager: boolean;
  isViewOnly: boolean;
  canManagePayments: boolean;
  canManageRefunds: boolean;
  canManageProducts: boolean;
  canViewFees: boolean;
  canAccessSettings: boolean;
  canManageTeam: boolean;
}

export function useShopifyMerchant(): ShopifyMerchantState {
  const { user, loading: authLoading } = useAuth();
  const [role, setRole] = useState<MerchantRole>(null);
  const [loading, setLoading] = useState(true);
  const [merchantId, setMerchantId] = useState<string | null>(null);
  const lastSelectionKey = useRef<string | null>(null);
  const embeddedShop = normalizeShopDomain(
    typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('shop') : null,
  );

  useEffect(() => {
    if (authLoading) {
      setLoading(true);
      return;
    }

    if (!user) {
      setRole(null);
      setMerchantId(null);
      setLoading(false);
      lastSelectionKey.current = null;
      return;
    }

    const storedSupportSession = getMerchantSupportSession();
    const selectionKey = `${user.id}:${embeddedShop || 'no-shop'}:${storedSupportSession?.sessionId || 'no-support-session'}`;

    if (lastSelectionKey.current === selectionKey) {
      return;
    }

    const fetchRole = async () => {
      setLoading(true);
      lastSelectionKey.current = selectionKey;

      try {
        if (storedSupportSession) {
          const { data: activeSupportSession, error: supportError } = await (supabase as any)
            .rpc('get_active_merchant_support_session');

          if (
            !supportError &&
            activeSupportSession?.session_id === storedSupportSession.sessionId &&
            activeSupportSession?.merchant_id === storedSupportSession.merchantId
          ) {
            setRole('super_admin');
            setMerchantId(storedSupportSession.merchantId);
            localStorage.setItem('active_merchant_id', storedSupportSession.merchantId);
            setLoading(false);
            return;
          }

          clearMerchantSupportSession();
        }

        const { data: merchantUsers, error } = await supabase
          .from('merchant_users')
          .select('merchant_id, role, merchants!inner(platform_type, shopify_store_domain)')
          .eq('user_id', user.id);

        if (error || !merchantUsers || merchantUsers.length === 0) {
          setRole(null);
          setMerchantId(null);
        } else {
          const storedMerchantId = localStorage.getItem('active_merchant_id');
          const activeRecord = getPreferredMerchantAssociation(merchantUsers as any[], {
            preferredShop: embeddedShop,
            storedMerchantId,
          });

          if (activeRecord) {
            setRole(activeRecord.role as MerchantRole);
            setMerchantId(activeRecord.merchant_id);
            localStorage.setItem('active_merchant_id', activeRecord.merchant_id);
          } else {
            setRole(null);
            setMerchantId(null);
          }
        }
      } catch (error) {
        console.error('Error fetching shopify merchant role:', error);
        setRole(null);
        setMerchantId(null);
      } finally {
        setLoading(false);
      }
    };

    fetchRole();
  }, [authLoading, embeddedShop, user]);

  const isSuperAdmin = role === 'super_admin';
  const isManager = role === 'manager';
  const isViewOnly = role === 'view_only';

  return {
    role,
    loading,
    merchantId,
    isSuperAdmin,
    isManager,
    isViewOnly,
    canManagePayments: isSuperAdmin || isManager,
    canManageRefunds: isSuperAdmin || isManager,
    canManageProducts: isSuperAdmin || isManager,
    canViewFees: isSuperAdmin,
    canAccessSettings: isSuperAdmin,
    canManageTeam: isSuperAdmin,
  };
}
